import { t as __exportAll } from "./chunk-rolldown-runtime.mjs";
import { n as memory_exports } from "./chunk-memory.mjs";
import { log } from "@dxos/log";
import * as localForage from "localforage";
import { TRACE_PROCESSOR } from "@dxos/tracing";
import * as Effect from "effect/Effect";
import { scheduleTaskInterval } from "@dxos/async";
import { Context } from "@dxos/context";
import * as FetchHttpClient from "effect/http/FetchHttpClient";
import * as HttpClient from "effect/http/HttpClient";
import * as HttpClientRequest from "effect/http/HttpClientRequest";
import * as Schema from "effect/Schema";
import * as Duration from "effect/Duration";
import * as Exit from "effect/Exit";
import * as FiberMap from "effect/FiberMap";
import * as Queue from "effect/Queue";
import * as Scope from "effect/Scope";
import * as Stream from "effect/Stream";
import * as Database from "@dxos/echo/Database";
import { ClientTraceEvents } from "@dxos/client-protocol";
import * as Fiber from "effect/Fiber";
import * as Schedule from "effect/Schedule";
//#region src/providers/event-loop-lag.ts
var event_loop_lag_exports = /* @__PURE__ */ __exportAll({
	EventLoopLagTracker: () => EventLoopLagTracker,
	LAG_SAMPLE_INTERVAL_MS: () => 500,
	LAG_WINDOW_MS: () => LAG_WINDOW_MS,
	MAX_PLAUSIBLE_LAG_MS: () => MAX_PLAUSIBLE_LAG_MS,
	eventLoopLagProvider: () => eventLoopLagProvider
});
var __dxlog_file$3 = "/__w/dxos/dxos/packages/sdk/observability/src/providers/event-loop-lag.ts";
var SECONDS = { unit: "s" };
/** Window the reported peak covers. Matches the metric export interval. */
var LAG_WINDOW_MS = 6e4;
/**
* Gap beyond which a late probe is read as the realm having been suspended rather than blocked.
*
* Browsers throttle or freeze timers in a hidden tab — and in the workers that tab owns — and the
* machine can sleep, so an arbitrarily long gap is always possible for reasons that are not jank.
* 10s because a main thread genuinely blocked that long means the app is hung rather than janky:
* past this point the distinction stops paying, and a false reading is worse than a missed one.
*/
var MAX_PLAUSIBLE_LAG_MS = 1e4;
/**
* Tracks how far behind schedule a timer fires, which is how long the realm's event loop was busy.
*
* Reports the peak of the last completed window rather than an instantaneous value: a gauge read
* only at collection time can never observe a stall, because a blocked loop delays the collection
* callback itself.
*
* The window is rotated by {@link rotate} rather than by the read, so {@link peakMs} is a plain
* idempotent getter. A read that consumed the value would break as soon as it ran more than once
* per window — which `RemoteMetrics` fan-out to a second processor, or a `flush()` landing next to
* a periodic collection, would both cause.
*/
var EventLoopLagTracker = class {
	_intervalMs;
	_maxPlausibleMs;
	#maxInWindowMs = 0;
	#peakMs = 0;
	#lastSampleAt;
	constructor(_intervalMs = 500, _maxPlausibleMs = MAX_PLAUSIBLE_LAG_MS) {
		this._intervalMs = _intervalMs;
		this._maxPlausibleMs = _maxPlausibleMs;
	}
	/**
	* Discards the reference timestamp so the next probe cannot report lag.
	* Called when the realm was legitimately not running — a hidden tab has its timers clamped to
	* once per second or worse, which would otherwise read as tens of seconds of event-loop lag.
	*/
	suspend() {
		this.#lastSampleAt = void 0;
	}
	/**
	* Records one probe. Lag is elapsed time beyond the scheduled interval.
	*
	* A gap past {@link MAX_PLAUSIBLE_LAG_MS} is discarded, not clamped: clamping would still report
	* a ceiling-valued stall that never happened. This has to hold without help from a visibility
	* listener, because when timers are frozen outright **no probe fires during the freeze** — so
	* nothing can mark the suspension before the first probe after it observes the entire gap.
	*/
	sample(now) {
		if (this.#lastSampleAt !== void 0) {
			const lagMs = Math.max(0, now - this.#lastSampleAt - this._intervalMs);
			if (lagMs > this._maxPlausibleMs) log("discarding implausible event loop lag", {
				lagMs,
				maxPlausibleMs: this._maxPlausibleMs
			}, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$3,
				L: 74,
				S: this
			});
			else this.#maxInWindowMs = Math.max(this.#maxInWindowMs, lagMs);
		}
		this.#lastSampleAt = now;
	}
	/** Peak lag observed during the last completed window. Safe to read any number of times. */
	get peakMs() {
		return this.#peakMs;
	}
	/** Closes the current window, publishing its peak. Driven on the export cadence. */
	rotate() {
		this.#peakMs = this.#maxInWindowMs;
		this.#maxInWindowMs = 0;
	}
};
/**
* Publishes how long this realm's event loop was blocked.
*
* Reports peak lag per export window, tagged only by the `dxos.process.type` resource attribute —
* so the same provider distinguishes the tab from the shared and dedicated workers without any
* per-realm wiring.
*/
var eventLoopLagProvider = () => Effect.fn(function* (observability) {
	const ctx = new Context(void 0, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file$3,
		L: 102
	});
	const lag = new EventLoopLagTracker(500);
	scheduleTaskInterval(ctx, async () => lag.sample(Date.now()), 500);
	const doc = globalThis.document;
	if (doc) {
		const onVisibilityChange = () => lag.suspend();
		doc.addEventListener("visibilitychange", onVisibilityChange);
		ctx.onDispose(() => doc.removeEventListener("visibilitychange", onVisibilityChange));
	}
	scheduleTaskInterval(ctx, async () => lag.rotate(), LAG_WINDOW_MS);
	ctx.onDispose(observability.metrics.observe("dxos.client.runtime.eventLoop.lag", () => lag.peakMs / 1e3, void 0, SECONDS));
	return async () => {
		await ctx.dispose();
	};
});
//#endregion
//#region src/providers/ip-data.ts
var ip_data_exports = /* @__PURE__ */ __exportAll({ provider: () => provider$3 });
var __dxlog_file$2 = "/__w/dxos/dxos/packages/sdk/observability/src/providers/ip-data.ts";
var IP_DATA_CACHE_TIMEOUT = 216e5;
var IPData = Schema.Struct({
	city: Schema.NullOr(Schema.String),
	region: Schema.NullOr(Schema.String),
	country_name: Schema.String,
	latitude: Schema.NullOr(Schema.Number),
	longitude: Schema.NullOr(Schema.Number)
});
var getIPData = Effect.fn(function* (config) {
	const httpClientNoTrace = (yield* HttpClient.HttpClient).pipe(HttpClient.transformResponse(Effect.provideService(HttpClient.TracerDisabledWhen, () => true)));
	const cachedData = yield* Effect.promise(() => localForage.getItem("dxos:observability:ipdata:v2"));
	if (cachedData && cachedData.timestamp > Date.now() - IP_DATA_CACHE_TIMEOUT) return cachedData.data;
	const IPDATA_API_KEY = config.get("runtime.app.env.DX_IPDATA_API_KEY");
	if (!IPDATA_API_KEY) {
		log.warn("DX_IPDATA_API_KEY is not configured; IP geolocation tags will be absent from telemetry", void 0, {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file$2,
			L: 55,
			S: this
		});
		return cachedData?.data;
	}
	const data = yield* HttpClientRequest.get(`https://api.ipdata.co?api-key=${IPDATA_API_KEY}`).pipe(httpClientNoTrace.execute, Effect.flatMap((res) => res.json), Effect.flatMap(Schema.decodeUnknownEffect(IPData)), Effect.catch((err) => Effect.sync(() => {
		log.warn("ipdata fetch failed; IP geolocation tags will be absent or stale", { err }, {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file$2,
			L: 66,
			S: this
		});
		return cachedData?.data;
	})));
	if (data) yield* Effect.promise(() => localForage.setItem("dxos:observability:ipdata:v2", {
		data,
		timestamp: Date.now()
	}));
	return data;
});
/** Fetches IP geolocation data and sets city/region/country tags on the observability instance. */
var provider$3 = (config) => (observability) => Effect.gen(function* () {
	const ipData = yield* getIPData(config);
	if (!ipData) return;
	observability.setTags({
		...ipData.city != null && { city: ipData.city },
		...ipData.region != null && { region: ipData.region },
		country: ipData.country_name,
		...ipData.latitude != null && { latitude: ipData.latitude },
		...ipData.longitude != null && { longitude: ipData.longitude }
	});
}).pipe(Effect.provide(FetchHttpClient.layer), Effect.catch((err) => Effect.sync(() => {
	log.warn("ipdata provider failed", { err }, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file$2,
		L: 100,
		S: void 0
	});
})));
//#endregion
//#region src/providers/object-events.ts
var object_events_exports = /* @__PURE__ */ __exportAll({
	DRAFT_WINDOW: () => DRAFT_WINDOW,
	EVENTS: () => EVENTS$1,
	listen: () => listen$1,
	provider: () => provider$2
});
var __dxlog_file$1 = "/__w/dxos/dxos/packages/sdk/observability/src/providers/object-events.ts";
/** The product events ECHO's trace events are reported as. */
var EVENTS$1 = {
	objectAdd: "space.object.add",
	objectRemove: "space.object.remove",
	relationAdd: "space.relation.add",
	relationRemove: "space.relation.remove",
	typeAdd: "space.type.add",
	feedAppend: "space.feed.append"
};
/**
* How long a new object must survive before it counts as created: the create dialog adds a draft when it
* opens and removes it if the user cancels.
*/
var DRAFT_WINDOW = Duration.seconds(30);
/**
* Reports ECHO's trace events as product events: user-facing objects and every relation added or removed, types
* persisted, and items appended to feeds. Every write is reported with its `origin` (`user`, `system` or `unknown`),
* so a dashboard separates people's actions from the rest. An add that is removed within the draft window
* reports neither. Events arrive only while registered; ECHO does not replay them.
*/
var listen$1 = (events, capture, draftWindow = DRAFT_WINDOW) => Effect.gen(function* () {
	const queue = yield* Queue.unbounded();
	const processor = { emit: (name, attributes) => Queue.offerUnsafe(queue, {
		name,
		attributes
	}) };
	yield* Effect.acquireRelease(Effect.sync(() => events.registerProcessor(processor)), () => Effect.sync(() => events.unregisterProcessor(processor)));
	const send = (event, properties) => Effect.try(() => capture(event, properties)).pipe(Effect.catch((error) => Effect.sync(() => log.catch(error, void 0, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file$1,
		L: 60,
		S: this
	}))));
	const pending = yield* FiberMap.make();
	yield* Stream.fromQueue(queue).pipe(Stream.runForEach(({ name, attributes }) => Effect.gen(function* () {
		const { spaceId, objectId, typename, relation, userType, origin } = attributes;
		const draftKey = `${String(spaceId)}/${String(objectId)}`;
		switch (name) {
			case Database.TraceEvents.objectAdd: {
				if (typeof objectId !== "string" || relation !== true && userType !== true) return;
				const event = relation === true ? EVENTS$1.relationAdd : EVENTS$1.objectAdd;
				yield* FiberMap.run(pending, draftKey, Effect.sleep(draftWindow).pipe(Effect.andThen(send(event, {
					spaceId,
					objectId,
					typename,
					origin
				}))));
				return;
			}
			case Database.TraceEvents.objectRemove:
				if (typeof objectId !== "string") return;
				if (yield* FiberMap.has(pending, draftKey)) {
					yield* FiberMap.remove(pending, draftKey);
					return;
				}
				if (relation === true || userType === true) {
					const event = relation === true ? EVENTS$1.relationRemove : EVENTS$1.objectRemove;
					yield* send(event, {
						spaceId,
						objectId,
						typename,
						origin
					});
				}
				return;
			case Database.TraceEvents.typeAdd:
				yield* send(EVENTS$1.typeAdd, {
					spaceId,
					typename,
					version: attributes.version,
					origin
				});
				return;
			case Database.TraceEvents.feedAppend:
				yield* send(EVENTS$1.feedAppend, {
					spaceId,
					feedId: attributes.feedId,
					objectId,
					typename,
					origin
				});
				return;
		}
	})), Effect.forkScoped);
});
/**
* Reports what is written in this realm's spaces, however it was written: from a dialog, an agent, a sync, or a raw
* `db.add` in a view.
*/
var provider$2 = Effect.fn(function* (observability) {
	const scope = yield* Scope.make();
	yield* listen$1(TRACE_PROCESSOR.remoteEvents, (event, attributes) => observability.events.captureEvent(event, attributes)).pipe(Scope.provide(scope));
	return () => {
		Effect.runFork(Scope.close(scope, Exit.void));
	};
});
//#endregion
//#region src/providers/space-events.ts
var space_events_exports = /* @__PURE__ */ __exportAll({
	EVENTS: () => EVENTS,
	listen: () => listen,
	provider: () => provider$1,
	toProductEvent: () => toProductEvent
});
var __dxlog_file = "/__w/dxos/dxos/packages/sdk/observability/src/providers/space-events.ts";
/** The product events the client's trace events are reported as, by invitation kind. */
var EVENTS = {
	spaceCreate: "space.create",
	spaceShare: "space.share",
	spaceJoin: "space.join",
	spaceAdmit: "space.admit",
	deviceInvite: "identity.device.invite",
	deviceJoin: "identity.join",
	deviceAdmit: "identity.device.admit"
};
/** Maps one of the client's trace events to the product event it stands for, if any. */
var toProductEvent = (name, attributes) => {
	const { spaceId, kind, authMethod, multiUse, origin } = attributes;
	const device = kind === "device";
	switch (name) {
		case ClientTraceEvents.spaceCreate: return {
			name: EVENTS.spaceCreate,
			properties: {
				spaceId,
				origin
			}
		};
		case ClientTraceEvents.invitationCreate: return device ? {
			name: EVENTS.deviceInvite,
			properties: {
				authMethod,
				multiUse
			}
		} : {
			name: EVENTS.spaceShare,
			properties: {
				spaceId,
				authMethod,
				multiUse
			}
		};
		case ClientTraceEvents.invitationAccept: return device ? {
			name: EVENTS.deviceJoin,
			properties: {}
		} : {
			name: EVENTS.spaceJoin,
			properties: { spaceId }
		};
		case ClientTraceEvents.invitationAdmit: return device ? {
			name: EVENTS.deviceAdmit,
			properties: { multiUse }
		} : {
			name: EVENTS.spaceAdmit,
			properties: {
				spaceId,
				multiUse
			}
		};
		default: return;
	}
};
/** Reports the client's space and invitation trace events as product events until the returned cleanup runs. */
var listen = (events, capture) => {
	const processor = { emit: (name, attributes) => {
		const event = toProductEvent(name, attributes);
		if (event === void 0) return;
		try {
			capture(event.name, event.properties);
		} catch (error) {
			log.catch(error, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file,
				L: 60,
				S: void 0
			});
		}
	} };
	events.registerProcessor(processor);
	return () => events.unregisterProcessor(processor);
};
/**
* Reports spaces this realm creates and invitations it shares, accepts and admits guests through, however they
* were started: from a dialog, an operation, an agent or a deep link.
*/
var provider$1 = (observability) => Effect.sync(() => listen(TRACE_PROCESSOR.remoteEvents, (event, attributes) => observability.events.captureEvent(event, attributes)));
//#endregion
//#region src/providers/storage.ts
var storage_exports = /* @__PURE__ */ __exportAll({ provider: () => provider });
var provider = Effect.fn(function* (observability) {
	if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
		const fiber = Effect.gen(function* () {
			const storageEstimate = yield* Effect.tryPromise(() => navigator.storage.estimate());
			storageEstimate.usage && observability.metrics.gauge("storageUsage", storageEstimate.usage);
			storageEstimate.quota && observability.metrics.gauge("storageQuota", storageEstimate.quota);
		}).pipe(Effect.repeat(Schedule.fixed(Duration.hours(1))), Effect.runFork);
		return () => Effect.runSync(Fiber.interrupt(fiber));
	}
});
//#endregion
//#region src/providers/ObservabilityProvider.ts
var ObservabilityProvider_exports = /* @__PURE__ */ __exportAll({
	EventLoopLag: () => event_loop_lag_exports,
	IPData: () => ip_data_exports,
	Memory: () => memory_exports,
	ObjectEvents: () => object_events_exports,
	SpaceEvents: () => space_events_exports,
	Storage: () => storage_exports
});
//#endregion
export { ip_data_exports as a, object_events_exports as i, storage_exports as n, event_loop_lag_exports as o, space_events_exports as r, ObservabilityProvider_exports as t };

//# sourceMappingURL=chunk-ObservabilityProvider.mjs.map